import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useQuery } from '@truepill/tpos-react-router'
import type { StateComplianceResult } from '@truepill/tpos-types'
import { LaunchDarkly, LocationsLegacyIds, OrderStatus, OrderType, UserRoles } from '@truepill/tpos-types'
import AccessDenied from 'components/AccessDenied'
import AttachmentsPane from 'components/AttachmentsPane'
import BreadCrumb from 'components/BreadCrumb'
import LoadingSpinner from 'components/Loading'
import MessagesPane from 'components/MessagesPane'
import NotesPane from 'components/NotesPane'
import { useOrderPageNotesFilters } from 'components/NotesPane/NotesList/filters'
import { OrderEntry } from 'components/Order'
import OrderHeading from 'components/OrderHeading'
import OrderTopBanner from 'components/OrderTopBanner'
import OTCPackingItemListAccuracy from 'components/OTCPackingItemList/OTCPackingItemListAccuracy'
import PackingMaterialsList from 'components/PackingMaterialsList'
import {
  FilledHeader,
  PageContainer,
  PageContent,
  PageHeading,
  StickyPageHeadingContainer,
  UnPaddedPageContainer,
} from 'components/PageStructure'
import { EditMode } from 'components/RXPageStructure'
import { ListRowLabel, ListRowValue, RXCell } from 'components/RXTable'
import ToolBar, {
  EditButton,
  LogButton,
  MedicalHistoryButton,
  NotesButton,
  ReturnItemListButton,
  SplitOrdersButton,
} from 'components/ToolBar'
import TopBanner from 'components/TopBanner'
import { OrderWarningTopBanner } from 'components/TopBanner/OrderWarningTopBanner'
import TriageIssues from 'components/TriageIssues'
import UserEventTimer from 'components/UserEventTimer'
import { VALIDATE_ORDER_STATE_COMPLIANCE } from 'gql'
import { Box, CheckBox, Grid } from 'grommet'
import { useEditMode, useEditValue, useOrderLock, useStateWithDelay, useUnlockOrder } from 'hooks'
import useLogs from 'hooks/navigation/useLogs'
import useOrder from 'hooks/navigation/useOrder'
import useErrorToast from 'hooks/toast/useErrorToast'
import useIsOtcOnlyUser from 'hooks/useIsOtcOnlyUser'
import useLogPageView from 'hooks/useLogPageView'
import useCriticalNotesModal from 'modals/useCriticalNotesModal'
import useExcessiveCsRejectionsModal from 'modals/useExcessiveCsRejectionsModal'
import moment from 'moment'
import { useClient, useFlag } from 'providers/LaunchDarklyProvider'
import { usePlusClient } from 'providers/VisionRouter'
import { goToViewPatient } from 'routes'
import styled from 'styled-components'
import { bodySecondaryColor, contrastBackgroundColor, primaryColor, primaryColorDark } from 'styles/styleVariables'
import type { Address, GuestPatient, Log, Order, Patient, ShippingMethod, RXFillRequest } from 'types'
import { PaymentType } from 'types'
import { getPatientFromOrder, getUniqueOrderPatients, isOrderReadOnly } from 'utils'
import ActionButtons from './ActionButtons'
import ApiRequestDetails from './ApiRequestDetails'
import { PaymentAndShipping } from './components/PaymentAndShipping'
import { EventLog } from './EventLog'
import GuestPatientDetails from './GuestPatientDetails'
import OrderViewMode from './OrderViewMode'
import PackingTasks from './PackingTasks'
import PatientDetails from './PatientDetails'
import References from './References'

export { default as OrderViewMode } from './OrderViewMode'

const BoldGenderText = styled.span`
  font-weight: bold;
`

const NormalFontPatientInfo = styled.span`
  color: ${bodySecondaryColor};
  margin-top: 20px;
  display: flex;
  gap: 5px;
`

const PatientName = styled.span`
  text-decoration: underline;
  color: ${primaryColor};
  visited {
    color: ${primaryColor};
  }
`

const PrescriptionInfoWrapper = styled.span`
  display: flex;
  gap: 10px;
`

const PrescriptionsList = styled.ul`
  border: 0.25rem solid ${contrastBackgroundColor};
  border-radius: 0.25rem;
  background-color: ${contrastBackgroundColor};
  > li {
    background-color: transparent;
    border-color: transparent;
    margin-top: 0;
    margin-bottom: 0.25rem;
    border: none;
  }
`
const SubTitle = styled.h4`
  font-weight: 500;
  font-size: 1.1em;
  margin-top: 1.25rem;
  margin-bottom: 0.625rem;
`

const FillList = (props: { order: Order; onClickOrderEntry?: () => void; inModal?: boolean }) => {
  const { order, onClickOrderEntry, inModal } = props
  const { search } = useLocation()
  const isOtcOnlyUser = useIsOtcOnlyUser()
  const isMultiPetOrder = order?.orderType === OrderType.MultiPet

  // Check if rxFillRequests is available and has data
  const rxFillRequests = order?.rxFillRequests
  if (!rxFillRequests || rxFillRequests.length === 0) {
    return null
  }

  // Group rxFillRequests by patient, making sure we are using fill.patient._id
  const groupByPatient = rxFillRequests.reduce((group, fill) => {
    const fillPatientId = fill.patient._id
    if (!group.has(fillPatientId)) {
      group.set(fillPatientId, { patient: fill.patient, fills: [] })
    }
    group.get(fillPatientId)!.fills.push(fill)
    return group
  }, new Map<string, { patient: Patient; fills: RXFillRequest[] }>())

  return (
    <Box data-testid="prescriptions">
      {[...groupByPatient.entries()].map(([patientId, group], index) => {
        const { patient, fills } = group

        return (
          <div key={patientId || index}>
            <PrescriptionInfoWrapper>
              <SubTitle>Prescriptions</SubTitle>
              {isMultiPetOrder && (
                //Display patient information only if it's a MultiPetOrder
                <NormalFontPatientInfo data-private>
                  {isOtcOnlyUser ? (
                    <PatientName data-private>{`${patient.firstName} ${patient.lastName}`}</PatientName>
                  ) : (
                    <Link target="_blank" rel="noopener noreferrer" to={goToViewPatient({ patientId, search })}>
                      <PatientName data-private>{`${patient.firstName} ${patient.lastName}`}</PatientName>
                    </Link>
                  )}
                  {moment(patient.dob).format('MM/DD/YYYY')},
                  <BoldGenderText>{patient.gender.charAt(0).toUpperCase()} </BoldGenderText>
                  {patient.species}
                </NormalFontPatientInfo>
              )}
            </PrescriptionInfoWrapper>
            <PrescriptionsList>
              <OrderEntry
                // TODO/Tech-debt: Consider split this component into multiple variations since it's doing too much already
                showFillsOnly={true}
                order={{ ...order, rxFillRequests: fills }}
                onClick={onClickOrderEntry}
                inline
                hideOrderDetails
                editableEntries={!inModal}
                isRxComingFromOrderPage
              />
            </PrescriptionsList>
          </div>
        )
      })}
    </Box>
  )
}

/**
 * This component intentionally doesn't use the FormDataProvider setup because
 * it's neatly self-contained and so doing everything here sidesteps issues with
 * nested FormDataProviders. This pattern with `useStateWithDelay` can be used
 * to do similar cleanup elsewhere for any editing self-contained under a single
 * component (including the action buttons).
 */

type OrderViewProps = {
  inModal?: boolean
  orderId: string
  mode: OrderViewMode
  onClickOrderEntry?: () => void
}

const useNotesBadgeCounter = (notesFilters: ReturnType<typeof useOrderPageNotesFilters>, notes: Log[]) => {
  const count = useMemo(() => {
    const orderLevelNotes = notesFilters.Order(notes)
    const fillLevelNotes = notesFilters.Fill(notes)
    return [...orderLevelNotes, ...fillLevelNotes].filter(
      (item, index, arr) => index === arr.findIndex(_item => _item._id === item._id),
    ).length
  }, [notes, notesFilters])

  return count
}

const OrderView = ({ inModal = false, orderId, mode, onClickOrderEntry }: OrderViewProps): JSX.Element => {
  const { order, apiRequest, loading, error } = useOrder(orderId)
  const { tokenContext } = usePlusClient()
  const { hasRole } = tokenContext
  const isOtcOnlyUser = useIsOtcOnlyUser()

  useLogPageView(
    {
      page: `order/${order?.status}${order?.inTriage ? '/Triage' : ''}`,
      orderId: orderId,
    },
    Boolean(!loading && order),
  )

  const isMultiPetOrder = order?.orderType === OrderType.MultiPet
  const patient = !isMultiPetOrder ? getPatientFromOrder(order) : undefined
  const uniquePatients = isMultiPetOrder ? getUniqueOrderPatients(order) : undefined
  const customer = order?.customer

  const ohioLocationLegacyIds = [
    LocationsLegacyIds.HAMILTON,
    LocationsLegacyIds.HAMILTON_2,
    LocationsLegacyIds.HAMILTON_3,
    LocationsLegacyIds.SEVEN_HILLS,
  ]

  // Check if any prescription is outside Ohio 6-month window
  const tooOldForOhio =
    order?.location?.legacyId &&
    ohioLocationLegacyIds.includes(order.location.legacyId) &&
    order?.rxFillRequests?.some(fill => {
      const prescription = fill.prescription
      if (!prescription) return false

      // Check if this is truly a first fill, considering Direct Transfer fill history
      const isFirstFill =
        prescription.totalQuantity === prescription.quantityRemaining &&
        !prescription.directTransfer?.prescription?.lastFillDate

      // Skip if not a first fill
      if (!isFirstFill) {
        return false
      }

      // For first fills, check if written date is older than 6 months
      if (!prescription.writtenDate || moment().diff(moment(prescription.writtenDate), 'months') < 6) {
        return false
      }

      // If written date is older than 6 months and it's a true first fill, it's not eligible
      return true
    })

  const { ldReady, ldIdentify } = useClient()
  const { setOrderLock, clearOrderLock, orderLockedBy, isOrderLockedByMe, acquiredByMeRef } = useOrderLock(orderId)
  const showErrorToast = useErrorToast()
  const coreOrderId = order?.coreOrderId
  const { data } = useQuery<{ validateOrderStateCompliance: StateComplianceResult }>(VALIDATE_ORDER_STATE_COMPLIANCE, {
    variables: { coreOrderId },
    skip: !coreOrderId || isOtcOnlyUser,
  })

  // Should not include the patient related logs for the order logs list.
  const { logs } = useLogs({ orderId })

  const { logs: notes, loading: isLoadingNotes } = useLogs(
    {
      ...(isMultiPetOrder ? { patientIds: uniquePatients?.map(patient => patient?._id) } : { patientId: patient?._id }),
      orderId,
    },
    { shouldIncludePatientRelatedLogs: true },
  )
  const [editValue] = useEditValue()
  const [editMode] = useEditMode()
  const [patientValues, setPatientValues] = useStateWithDelay<Patient>(patient, loading)
  const [guestPatientValues, setGuestPatientValues] = useStateWithDelay<GuestPatient>(order?.guestPatient, loading)
  const [shippingAddressValues, setShippingAddressValues] = useStateWithDelay<Address>(order?.shippingAddress, loading)
  const [shippingMethod, setShippingMethod] = useStateWithDelay<ShippingMethod>(order?.shippingMethod, loading)
  const [paymentType, setPaymentType] = useStateWithDelay<PaymentType>(order?.paymentType || PaymentType.cash, loading)
  const [isReplacement, setIsReplacement] = useStateWithDelay<boolean>(order?.isReplacement, loading)
  const [isReservedForInvestigation, setIsReservedForInvestigation] = useStateWithDelay<boolean>(
    order?.isReservedForInvestigation,
    loading,
  )

  const [trackingNumber, setTrackingNumber] = useState<string>('')
  const [otcSkuValues, setOtcSkuValues] = useStateWithDelay<{ [id: string]: string | null }>(
    Object.fromEntries(order?.otcProducts?.map(({ _id, sku }) => [_id, sku]) ?? []),
    loading,
  )
  const isReadOnly = isOrderReadOnly(order)
  const disableOrder: boolean | undefined = (!isOrderLockedByMe && !!orderLockedBy) || isReadOnly

  const decoupleCriticalNotes = useFlag(LaunchDarkly.FeatureFlags.DECOUPLE_CRITICAL_NOTES)
  const displayStripeId = useFlag(LaunchDarkly.FeatureFlags.DISPLAY_STRIPE_ID_IN_UI)

  const notesFilters = useOrderPageNotesFilters({ order })
  const badgeCount = useNotesBadgeCounter(notesFilters, notes ?? [])
  const filteredNotes = notes?.filter(note => note.event === 'note' && !note.isArchived)
  const notesBadgeCount = decoupleCriticalNotes ? filteredNotes?.length : badgeCount

  useCriticalNotesModal({
    order,
    orderOnlyNotes: true,
    logs: notes,
    showPatientNotes: true,
    showPrescriberNotes: true,
  })
  useExcessiveCsRejectionsModal({ order, logs: notes })
  useUnlockOrder(orderId, clearOrderLock, acquiredByMeRef)

  useEffect(() => {
    if (!orderLockedBy?._id) {
      setOrderLock().catch(e => showErrorToast(`Failed to lock order ${(e as Record<string, string>).message}`))
    }
  }, [orderId, orderLockedBy?._id, setOrderLock, showErrorToast])

  useEffect(() => {
    if (customer?.legacyId && ldReady) {
      ldIdentify({ key: customer.legacyId.toString() })
    }
  }, [ldReady, ldIdentify, customer])

  const onCancel = useCallback(() => {
    !!order?.guestPatient && setGuestPatientValues(order.guestPatient)
    !!order?.otcProducts &&
      setOtcSkuValues(Object.fromEntries(order?.otcProducts?.map(({ _id, sku }) => [_id, sku]) ?? []))
    !!patient && setPatientValues(patient)
    !!order?.shippingAddress && setShippingAddressValues(order.shippingAddress)
    !!order?.shippingMethod && setShippingMethod(order.shippingMethod)
    !!order?.paymentType && setPaymentType(order.paymentType || PaymentType.cash)
    typeof order?.isReplacement !== 'undefined' && setIsReplacement(order.isReplacement)
    typeof order?.isReservedForInvestigation !== 'undefined' &&
      setIsReservedForInvestigation(order.isReservedForInvestigation)
  }, [
    patient,
    order?.guestPatient,
    order?.otcProducts,
    order?.shippingAddress,
    order?.shippingMethod,
    order?.isReplacement,
    order?.paymentType,
    order?.isReservedForInvestigation,
    setPatientValues,
    setGuestPatientValues,
    setOtcSkuValues,
    setShippingAddressValues,
    setShippingMethod,
    setPaymentType,
    setIsReplacement,
    setIsReservedForInvestigation,
  ])

  useEffect(() => {
    const shipments = order?.shipments ?? []
    if (shipments.length) setTrackingNumber(shipments[shipments.length - 1].trackingNumber)
  }, [order?.shipments])

  useEffect(() => {
    if (order?.paymentType) setPaymentType(order?.paymentType)
  }, [order?.paymentType, setPaymentType])

  let innerContents = <></>
  const orderIsInPackingStatus = order?.status === OrderStatus.Packing

  if (error) {
    const isForbidden = error.graphQLErrors?.some(gqlError => gqlError.extensions?.code === 'FORBIDDEN')
    if (isForbidden) {
      return <AccessDenied />
    }
    return (
      <UnPaddedPageContainer>
        <p>Error loading Order {JSON.stringify(error)}</p>
      </UnPaddedPageContainer>
    )
  }

  if (loading || !order) {
    return <LoadingOrder />
  }

  const isReservedForInvestigationCheckbok = (
    <EditMode>
      <RXCell>
        <ListRowLabel>Reserved:</ListRowLabel>
        <ListRowValue>
          <CheckBox
            label="Yes"
            checked={isReservedForInvestigation}
            disabled={
              !hasRole([UserRoles.Admin, UserRoles.LeadPharmacist, UserRoles.Pharmacist, UserRoles.LeadCustomerSupport])
            }
            onChange={async e => {
              const { checked } = e.target
              setIsReservedForInvestigation(checked)
            }}
          />
        </ListRowValue>
      </RXCell>
    </EditMode>
  )

  const isReplacementCheckBox = (
    <EditMode>
      <RXCell>
        <ListRowLabel>Replacement:</ListRowLabel>
        <ListRowValue>
          <CheckBox
            label="Yes"
            disabled={order?.isReplacement}
            checked={isReplacement}
            onChange={async e => {
              const { checked } = e.target
              setIsReplacement(checked)
            }}
          />
        </ListRowValue>
      </RXCell>
    </EditMode>
  )

  if (!loading) {
    innerContents = (
      <>
        <Grid data-testid="order" columns={['flex', 'flex', 'flex']} gap="0.625rem">
          {/* Do not display this section if isMultiPetOrder is true*/}
          {!isMultiPetOrder && (
            <Box data-test-column="patient">
              <FilledHeader>Patient</FilledHeader>
              {!!patient && !!patientValues && (
                <PatientDetails
                  condensedView={orderIsInPackingStatus}
                  patient={patient}
                  patientValues={patientValues}
                  setPatientValues={value => setPatientValues(value as Patient)}
                />
              )}
              {!!order?.guestPatient && !!guestPatientValues && (
                <GuestPatientDetails
                  guestPatient={order.guestPatient as GuestPatient}
                  guestPatientValues={guestPatientValues}
                  setGuestPatientValues={setGuestPatientValues}
                />
              )}
            </Box>
          )}
          <Box data-test-column="payment">
            <FilledHeader>Payment and Shipping</FilledHeader>
            <PaymentAndShipping
              condensedView={orderIsInPackingStatus}
              disableOrder={disableOrder}
              order={order}
              paymentType={paymentType}
              setShippingAddressValues={setShippingAddressValues}
              setShippingMethod={setShippingMethod}
              setTrackingNumber={setTrackingNumber}
              shippingAddressValues={shippingAddressValues}
              shippingMethod={shippingMethod}
              trackingNumber={trackingNumber}
            />
          </Box>
          {inModal && (
            <Box data-test-column="references">
              <>
                <FilledHeader>References</FilledHeader>
                <RXCell data-test-row="notes">
                  <ListRowLabel>Customer notes:</ListRowLabel>
                  <ListRowValue>{apiRequest?.notes || 'None provided'}</ListRowValue>
                </RXCell>
              </>
            </Box>
          )}
          <Box key={trackingNumber} data-test-column="additional" data-test-viewmode={OrderViewMode[mode]}>
            {mode === OrderViewMode.Fulfillment && order.status === OrderStatus.Packing && (
              <>
                <FilledHeader>Tasks</FilledHeader>
                <PackingTasks
                  key={order?._id}
                  order={order}
                  trackingNumber={trackingNumber}
                  setTrackingNumber={setTrackingNumber}
                />
              </>
            )}

            {mode === OrderViewMode.Fulfillment && order.status === OrderStatus.Complete && (
              <>
                <FilledHeader>Event log</FilledHeader>
                <EventLog logs={logs} createdAt={order.createdAt} />
              </>
            )}

            {mode === OrderViewMode.Fulfillment &&
              [OrderStatus.PV1, OrderStatus.Fill, OrderStatus.PV2].includes(order.status) && (
                <>
                  <FilledHeader>References</FilledHeader>
                  {isReplacementCheckBox}
                  {isReservedForInvestigationCheckbok}
                </>
              )}

            {mode === OrderViewMode.Fulfillment && order.status === OrderStatus.Adjudication && (
              <EditMode>
                <FilledHeader>References</FilledHeader>
                {isReservedForInvestigationCheckbok}
              </EditMode>
            )}

            {mode === OrderViewMode.Pharmacy && (
              <>
                <FilledHeader>References</FilledHeader>
                <References order={order} displayStripeId={displayStripeId} />
                {isReplacementCheckBox}
                {order.status !== OrderStatus.Cancelled && isReservedForInvestigationCheckbok}
              </>
            )}
          </Box>
        </Grid>
        {/**  Render multiple FillList items for multiple pet patients or a single list if it's a single patient */}
        <FillList order={order} inModal={inModal} onClickOrderEntry={onClickOrderEntry} />
        {order.otcProducts.length > 0 && (
          <>
            <SubTitle>OTC</SubTitle>
            <OTCPackingItemListAccuracy
              order={order}
              scanningDisabled={
                mode !== OrderViewMode.Fulfillment || order.status !== OrderStatus.Packing || !!order.inTriage
              }
            />
          </>
        )}
        {!!order.packingMaterials?.length && (
          <>
            <SubTitle>Packing List</SubTitle>
            <PackingMaterialsList order={order} />
          </>
        )}
        {!orderIsInPackingStatus && <ApiRequestDetails apiRequest={apiRequest} />}
      </>
    )
  }

  if (inModal) {
    return (
      <Box id="OrderView" direction="column" margin="0" width="80rem">
        {innerContents}
      </Box>
    )
  }

  const userEvent =
    mode === OrderViewMode.Fulfillment && order.inTriage
      ? 'triage'
      : mode === OrderViewMode.Fulfillment && order.status === OrderStatus.Packing
      ? 'packing'
      : 'view'
  return (
    <>
      <UserEventTimer event={userEvent} orderId={order._id} />
      {order.inTriage && <TopBanner message="Triage" />}
      {data?.validateOrderStateCompliance?.isRejectable && (
        <OrderWarningTopBanner messages={data?.validateOrderStateCompliance?.messages} />
      )}
      {tooOldForOhio && (
        <OrderWarningTopBanner
          messages={[
            'Cannot dispense in Ohio – written date exceeds Ohio 6-month limit for Ohio pharmacies. Please reroute to a non-Ohio facility.',
          ]}
        />
      )}
      <PageContainer>
        <StickyPageHeadingContainer>
          <OrderTopBanner
            orderId={order._id}
            disableOrder={disableOrder}
            orderLockedBy={orderLockedBy}
            isReadOnly={isReadOnly}
          />
          <BreadCrumb />
          <PageHeading>
            <OrderHeading order={order} />
            {order && (
              <ActionButtons
                disableOrder={disableOrder}
                mode={mode}
                onCancel={onCancel}
                order={order}
                patientValues={patientValues}
                shippingAddressValues={shippingAddressValues}
                shippingMethod={shippingMethod}
                trackingNumber={trackingNumber}
                setTrackingNumber={setTrackingNumber}
                otcSkuValues={otcSkuValues}
                isReplacement={isReplacement}
                isReservedForInvestigation={isReservedForInvestigation}
                paymentType={paymentType}
              />
            )}
          </PageHeading>
        </StickyPageHeadingContainer>
        <PageContent>
          <Box direction="column" margin={{ left: '1.875rem' }} width="100rem">
            {order.inTriage && <TriageIssues item={order} />}
            {innerContents}
          </Box>
          <ToolBar>
            {!editValue && <EditButton orderId={orderId} disabled={disableOrder} />}
            <LogButton logs={logs} orderId={orderId} />
            <NotesButton notesCount={notesBadgeCount} showBadge={notesBadgeCount > 0} />
            <SplitOrdersButton order={order} siblingUrlToken={apiRequest?.sibling_url_token} />
            {mode === OrderViewMode.Fulfillment && !isMultiPetOrder && patient && (
              <MedicalHistoryButton patientId={patient._id} />
            )}
            {mode === OrderViewMode.Pharmacy && <ReturnItemListButton order={order} />}
          </ToolBar>
        </PageContent>
      </PageContainer>
      {order && <MessagesPane logs={notes} />}
      {order && (
        <NotesPane
          isLoadingLogs={isLoadingNotes}
          filterOutEncounterRecordsFromOtherThanAllTabs
          logs={notes}
          item={order}
          {...(isMultiPetOrder ? { patients: uniquePatients } : { patient: patient })}
          tabs={['Order', 'Patient']}
          defaultTab="Order"
          filters={notesFilters}
        />
      )}
      {order && !isOtcOnlyUser && <AttachmentsPane orderId={order._id} patientId={patientValues?._id} />}
    </>
  )
}

const LoadingOrder = () => (
  <UnPaddedPageContainer>
    <LoadingSpinnerContainer>
      <LoadingSpinner />
    </LoadingSpinnerContainer>
  </UnPaddedPageContainer>
)

const LoadingSpinnerContainer = styled.div`
  display: flex;
  padding-top: 15rem;
  justify-content: center;
  svg {
    height: 126px;
  }
`

export default OrderView
